import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Pill,
  Package,
  Truck,
  CheckCircle,
  Clock,
  RefreshCw,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PharmacyPage = () => {
  const { getAuthHeaders } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refillLoading, setRefillLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prescriptionsRes, deliveriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/prescriptions`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/pharmacy/delivery-status`, { headers: getAuthHeaders() })
      ]);
      setPrescriptions(prescriptionsRes.data);
      setDeliveries(deliveriesRes.data);
    } catch (error) {
      console.error('Error fetching pharmacy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefillRequest = async (prescriptionId) => {
    setRefillLoading(prescriptionId);
    try {
      await axios.post(`${API_URL}/api/prescriptions/refill/${prescriptionId}`, {}, {
        headers: getAuthHeaders()
      });
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error requesting refill:', error);
    } finally {
      setRefillLoading(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing': return <Clock className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Demo data for display
  const demoPrescriptions = [
    {
      id: '1',
      medication_name: 'Lisinopril 10mg',
      dosage: '10mg',
      frequency: 'Once daily',
      refills_remaining: 3,
      prescribed_date: '2025-01-15',
      pharmacy_name: 'Bell Pharmacy'
    },
    {
      id: '2',
      medication_name: 'Metformin 500mg',
      dosage: '500mg',
      frequency: 'Twice daily',
      refills_remaining: 2,
      prescribed_date: '2025-01-10',
      pharmacy_name: 'Bell Pharmacy'
    }
  ];

  const demoDeliveries = [
    {
      id: '1',
      medication_name: 'Lisinopril 10mg',
      status: 'shipped',
      tracking_number: 'TRK123456789',
      estimated_delivery: '2025-01-18'
    }
  ];

  const displayPrescriptions = prescriptions.length > 0 ? prescriptions : demoPrescriptions;
  const displayDeliveries = deliveries.length > 0 ? deliveries : demoDeliveries;

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12" data-testid="pharmacy-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Pill className="w-6 h-6 text-primary" />
            Pharmacy & Prescriptions
          </h1>
          <p className="text-muted-foreground text-sm">
            Manage your medications and track deliveries
          </p>
        </div>

        {/* Delivery Info Banner */}
        <Card className="mb-8 bg-gradient-to-r from-primary to-accent text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Truck className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Quick Pharmacy Delivery</h2>
                <p className="text-white/80 text-sm">
                  Partnered with Bell Pharmacy for fast prescription delivery across NY & NJ
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Prescriptions - 2 cols */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Pill className="w-5 h-5 text-primary" />
                  Your Prescriptions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : displayPrescriptions.length > 0 ? (
                  <div className="space-y-4">
                    {displayPrescriptions.map((rx) => (
                      <div 
                        key={rx.id}
                        className="p-4 border rounded-xl hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Pill className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{rx.medication_name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {rx.dosage} • {rx.frequency}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Prescribed: {new Date(rx.prescribed_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant={rx.refills_remaining > 0 ? 'default' : 'secondary'}>
                              {rx.refills_remaining} refills left
                            </Badge>
                            <div className="mt-2">
                              <Button
                                size="sm"
                                disabled={rx.refills_remaining === 0 || refillLoading === rx.id}
                                onClick={() => handleRefillRequest(rx.id)}
                                className={rx.refills_remaining > 0 ? 'btn-primary' : ''}
                              >
                                <RefreshCw className={`w-4 h-4 mr-1 ${refillLoading === rx.id ? 'animate-spin' : ''}`} />
                                Request Refill
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Pill className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No prescriptions on file</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Delivery Tracking - 1 col */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Truck className="w-5 h-5 text-primary" />
                  Delivery Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                {displayDeliveries.length > 0 ? (
                  <div className="space-y-4">
                    {displayDeliveries.map((delivery) => (
                      <div key={delivery.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{delivery.medication_name}</span>
                          <Badge className={getStatusColor(delivery.status)}>
                            {getStatusIcon(delivery.status)}
                            <span className="ml-1 capitalize">{delivery.status}</span>
                          </Badge>
                        </div>
                        {delivery.tracking_number && (
                          <p className="text-xs text-muted-foreground">
                            Tracking: {delivery.tracking_number}
                          </p>
                        )}
                        {delivery.estimated_delivery && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Est. delivery: {new Date(delivery.estimated_delivery).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No active deliveries</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Delivery Area */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Pharmacy Partner</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  We partner with Bell Pharmacy for quick delivery:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Bell Pharmacy (Primary Partner)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    New York (all boroughs)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    New Jersey (statewide)
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyPage;
